import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, Redirect, useParams } from 'react-router-dom';
import { useFunds, useMe, useTransactions } from '../../common/apiHooks';
import { Euro } from '../../common/Euro';
import { Shimmer } from '../../common/shimmer/Shimmer';
import { dayInTallinn, formatDateYear, timeInTallinn } from '../../common/dateFormatter';
import { usePageTitle } from '../../common/usePageTitle';
import { Fund, TransactionType, User } from '../../common/apiModels';
import { getBankName } from '../../common/iban';
import { TranslationKey } from '../../translations';
import { PII_CLASS } from '../../tracking/piiMarkup';
import { navScaleFor } from '../../common/fundPrecision';
import { Units } from '../../common/Units';

const TYPE_LABEL: Record<TransactionType, TranslationKey> = {
  CONTRIBUTION_CASH: 'transactions.detail.type.subscription',
  CONTRIBUTION_CASH_WORKPLACE: 'transactions.detail.type.subscription',
  SUBTRACTION: 'transactions.detail.type.redemption',
  TRANSFER_IN: 'transactions.detail.type.transferIn',
  TRANSFER_OUT: 'transactions.detail.type.transferOut',
};

function getBackPath(fund?: Fund): string {
  if (fund?.pillar === 2) {
    return '/2nd-pillar-transactions';
  }
  if (fund?.pillar === 3) {
    return '/3rd-pillar-transactions';
  }
  if (fund?.pillar === null) {
    return '/savings-fund-transactions';
  }
  return '/account';
}

function unitHolderName(user?: User): string | null {
  if (!user) {
    return null;
  }
  return user.role?.name ?? `${user.firstName} ${user.lastName}`;
}

export const TransactionDetailPage: React.FunctionComponent = () => {
  usePageTitle('pageTitle.transactionDetail');

  const { id } = useParams<{ id: string }>();
  const { data: transactions, isLoading: transactionsLoading } = useTransactions();
  const { data: funds = [], isLoading: fundsLoading } = useFunds();
  const { data: user, isLoading: userLoading } = useMe();

  if (transactionsLoading || fundsLoading || userLoading) {
    return (
      <section className="mt-5">
        <Shimmer height={200} />
      </section>
    );
  }

  const transaction = transactions?.find((t) => t.id === id);

  if (!transaction) {
    return <Redirect to="/account" />;
  }

  const fund = funds.find((f) => f.isin === transaction.isin);
  const isSavingsFund = fund?.pillar === null;
  const isRedemption = transaction.type === 'SUBTRACTION';
  const isTransfer = transaction.type === 'TRANSFER_IN' || transaction.type === 'TRANSFER_OUT';
  const holder = unitHolderName(user);
  const bankName = transaction.counterpartyIban && getBankName(transaction.counterpartyIban);

  return (
    <section className="mt-5">
      <div className="mt-5 mb-4 d-flex flex-sm-row flex-column align-items-baseline justify-content-between">
        <h2 className="m-0">
          <FormattedMessage id="transactions.detail.title" />
        </h2>
        <Link className="icon-link" to={getBackPath(fund)}>
          <FormattedMessage id="transactions.detail.back" />
        </Link>
      </div>
      <dl className={`row text-pretty ${PII_CLASS}`}>
        <dt className="col-sm-4 mb-sm-2 text-balance">
          <FormattedMessage id="transactions.detail.type" />
        </dt>
        <dd className="col-sm-8">
          <FormattedMessage id={TYPE_LABEL[transaction.type]} />
        </dd>

        <dt className="col-sm-4 mb-sm-2 text-balance">
          <FormattedMessage id="transactions.detail.fund" />
        </dt>
        <dd className="col-sm-8">{fund?.name ?? transaction.isin}</dd>

        <dt className="col-sm-4 mb-sm-2 text-balance">
          <FormattedMessage id="transactions.detail.amount" />
        </dt>
        <dd className="col-sm-8">
          <Euro amount={transaction.amount} />
        </dd>

        {transaction.units != null && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.units" />
            </dt>
            <dd className="col-sm-8">
              <Units units={transaction.units} />
            </dd>
          </>
        )}

        {transaction.nav != null && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.nav" />
            </dt>
            <dd className="col-sm-8">
              <Euro
                amount={transaction.nav}
                fractionDigits={navScaleFor(transaction.isin, transaction.nav)}
              />
            </dd>
          </>
        )}

        {transaction.acquisitionCost != null && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.acquisitionCost" />
            </dt>
            <dd className="col-sm-8">
              <Euro amount={transaction.acquisitionCost} />
            </dd>
          </>
        )}

        {isSavingsFund && transaction.applicationTime && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.applicationTime" />
            </dt>
            <dd className="col-sm-8">
              <FormattedMessage
                id="transactions.detail.applicationTime.value"
                values={{
                  date: formatDateYear(dayInTallinn(transaction.applicationTime)),
                  time: timeInTallinn(transaction.applicationTime),
                }}
              />
            </dd>
          </>
        )}

        {isSavingsFund && transaction.priceCalculationDate && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.priceCalculationDate" />
            </dt>
            <dd className="col-sm-8">{formatDateYear(transaction.priceCalculationDate)}</dd>
          </>
        )}

        <dt className="col-sm-4 mb-sm-2 text-balance">
          <FormattedMessage
            id={isSavingsFund ? 'transactions.detail.executionDate' : 'transactions.detail.date'}
          />
        </dt>
        <dd className="col-sm-8">{formatDateYear(dayInTallinn(transaction.time))}</dd>

        {isSavingsFund && !isTransfer && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.paymentMethod" />
            </dt>
            <dd className="col-sm-8">
              {transaction.counterpartyIban ? (
                <>
                  <FormattedMessage
                    id={
                      isRedemption
                        ? 'transactions.detail.paymentMethod.toAccount'
                        : 'transactions.detail.paymentMethod.fromAccount'
                    }
                    values={{ iban: transaction.counterpartyIban }}
                  />
                  {bankName && <div className="text-secondary">{bankName}</div>}
                </>
              ) : (
                <FormattedMessage id="transactions.detail.paymentMethod.bankTransfer" />
              )}
            </dd>
          </>
        )}

        {isSavingsFund && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.fees" />
            </dt>
            <dd className="col-sm-8">
              <Euro amount={0} />
            </dd>
          </>
        )}

        {isSavingsFund && holder && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.unitHolder" />
            </dt>
            <dd className="col-sm-8">{holder}</dd>
          </>
        )}

        {isSavingsFund && (
          <>
            <dt className="col-sm-4 mb-sm-2 text-balance">
              <FormattedMessage id="transactions.detail.fundManager" />
            </dt>
            <dd className="col-sm-8">
              <FormattedMessage id="transactions.detail.fundManager.value" />
            </dd>
          </>
        )}
      </dl>
    </section>
  );
};
